import { useState, useEffect, useLayoutEffect, useRef } from 'react';

import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { resetScrollForRouteChange, scrollToTop } from './utils/scrollToTop';

import { RouteSeo } from './seo';



// Layout components

import { HeaderLogo, MenuButton, FullScreenMenu } from './components/layout';

import CustomCursor from './components/CustomCursor';



// Pages

import {

  HomePage,

  WorkPage,

  AboutPage,

  PrivacyPage,

  ShopPage,

  CardPage,

  LandingTestPage,

  MenyaPage,

  NordvikPage,

  SolitairePage,

  RainAlleyPage,

  WraithPage,

  ShelterPage,

  BarScenePage,

  DojakaPage,

  KlmBoardPage,

  DiorBoardPage,

  MismoStudioPage,

  MescalinaPage,

  NotFoundPage,

} from './pages';



// Global styles only

import { ContactModalProvider, useContactModal } from './context/ContactModalContext';

import { SHOP_ENABLED } from './data/shop';

import './styles/globals.css';

const STANDALONE_ROUTES = new Set(['/card', '/landingtest']);



const AppContent = () => {

  const [menuOpen, setMenuOpen] = useState(false);

  const { openContact } = useContactModal();

  const navigate = useNavigate();

  const location = useLocation();

  const scrollHomeRef = useRef(false);

  const isStandaloneRoute = STANDALONE_ROUTES.has(location.pathname);



  /* Prima del paint: uccidi memoria GSAP e torna in cima */

  useLayoutEffect(() => {

    if (scrollHomeRef.current && location.pathname === '/') {

      scrollHomeRef.current = false;

    }

    resetScrollForRouteChange();

  }, [location.pathname]);



  /* Dopo mount figli: riallinea trigger e forza ancora scroll 0 */

  useEffect(() => {

    scrollToTop();

    const id = requestAnimationFrame(() => {

      scrollToTop();

      ScrollTrigger.refresh();

    });

    return () => cancelAnimationFrame(id);

  }, [location.pathname]);



  const handleNavigate = (target) => {

    if (target === 'work-page') {

      navigate('/work');

    } else if (target === 'about-page') {

      navigate('/about');

    } else if (target === 'shop-page') {
      navigate('/shop');

    } else if (target === 'home') {

      scrollHomeRef.current = true;

      navigate('/');

      scrollToTop();

    } else if (target === 'contact') {

      openContact();

    } else {

      navigate('/');

      setTimeout(() => {

        scrollToTop();

      }, 100);

    }

  };



  return (

    <div className={`app${isStandaloneRoute ? ' app--standalone' : ''}`}>

      <CustomCursor />

      {!isStandaloneRoute && (
        <>
          <HeaderLogo />

          <MenuButton isOpen={menuOpen} onClick={() => setMenuOpen(!menuOpen)} />

          <FullScreenMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            onNavigate={handleNavigate}
            onOpenContact={openContact}
          />
        </>
      )}



      <RouteSeo />

      <Routes location={location} key={location.pathname}>

        <Route path="/" element={<HomePage />} />

        <Route path="/work" element={<WorkPage />} />

        <Route path="/about" element={<AboutPage />} />

        <Route path="/privacy" element={<PrivacyPage />} />

        <Route path="/shop" element={SHOP_ENABLED ? <ShopPage /> : <Navigate to="/" replace />} />

        <Route path="/card" element={<CardPage />} />

        <Route path="/landingtest" element={<LandingTestPage />} />

        <Route path="/project/1" element={<Navigate to="/menya" replace />} />
        <Route path="/project/2" element={<Navigate to="/nordvik" replace />} />
        <Route path="/project/3" element={<Navigate to="/solitaire" replace />} />
        <Route path="/project/4" element={<Navigate to="/rain-alley" replace />} />
        <Route path="/project/5" element={<Navigate to="/wraith" replace />} />
        <Route path="/project/6" element={<Navigate to="/shelter" replace />} />
        <Route path="/project/7" element={<Navigate to="/bar-scene" replace />} />
        <Route path="/project/8" element={<Navigate to="/dojaka" replace />} />
        <Route path="/project/9" element={<Navigate to="/klm-board" replace />} />
        <Route path="/project/10" element={<Navigate to="/dior-board" replace />} />
        <Route path="/project/11" element={<Navigate to="/mismo-studio" replace />} />
        <Route path="/project/12" element={<Navigate to="/mescalina" replace />} />

        <Route path="/menya" element={<MenyaPage />} />

        <Route path="/nordvik" element={<NordvikPage />} />

        <Route path="/solitaire" element={<SolitairePage />} />

        <Route path="/rain-alley" element={<RainAlleyPage />} />

        <Route path="/wraith" element={<WraithPage />} />

        <Route path="/shelter" element={<ShelterPage />} />

        <Route path="/bar-scene" element={<BarScenePage />} />

        <Route path="/dojaka" element={<DojakaPage />} />

        <Route path="/klm-board" element={<KlmBoardPage />} />

        <Route path="/dior-board" element={<DiorBoardPage />} />

        <Route path="/mismo-studio" element={<MismoStudioPage />} />

        <Route path="/mescalina" element={<MescalinaPage />} />

        <Route path="*" element={<NotFoundPage />} />

      </Routes>

    </div>

  );

};



function App() {

  return (

    <BrowserRouter>

      <ContactModalProvider>

        <AppContent />

      </ContactModalProvider>

    </BrowserRouter>

  );

}



export default App;

